import { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Price } from 'domain/listing/value-objects';
import {
  PriceHistoryRecord,
  PriceHistoryTimeline,
  type PriceHistoryListingData,
} from 'domain/listing/value-objects';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { formatMlsNumber } from 'ui/mls-info/format-mls-info';
import { formatPrice } from 'ui/price/format-price';
import { formatRecency } from 'ui/recency/format-recency';
import { getDaysText } from 'utils/translations/i18n';
import type { SupportedCurrency } from 'utils/i18n-format-currency';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { getStatusLabelKey } from 'types/reso';
import { StandardStatus } from '@zoocasa/go-search';

export const DEFAULT_PRICE_HISTORY_VISIBLE_COUNT = 4;

export type PriceHistoryTimelineRecordView<T extends PriceHistoryListingData = PriceHistoryListingData> = {
  raw: T;
  id: number | null;
  addressPath: string | null;
  isCurrentListing: boolean;
  eventDate: Date | null;
  absoluteDateLabel: string;
  relativeDateLabel: string | null;
  status: StandardStatus;
  statusLabel: string;
  price: number | null;
  priceLabel: string;
  originalPriceLabel: string | null;
  daysOnMarket: number | null;
  daysOnMarketLabel: string;
  lifecycleDateLabel: string | null;
  lifecycleLabel: string;
  mlsLabel: string | null;
  thumbnailSrc: string | null;
  thumbnailAlt: string;
};

export type PriceHistoryTimelineMonthGroupView<T extends PriceHistoryListingData = PriceHistoryListingData> = {
  monthKey: string;
  monthLabel: string;
  title: string;
  titleStatus: StandardStatus;
  titleStatusLabel: string;
  titlePriceLabel: string;
  latestRecord: PriceHistoryTimelineRecordView<T>;
  records: PriceHistoryTimelineRecordView<T>[];
};

export type UsePriceHistoryTimelineOptions<T extends PriceHistoryListingData = PriceHistoryListingData> = {
  records?: readonly T[] | null;
  currentListingId: number | null;
  visibleCount?: number;
};

export type UsePriceHistoryTimelineResult<T extends PriceHistoryListingData = PriceHistoryListingData> = {
  hasValue: boolean;
  stats: {
    lastSoldPriceLabel: string;
    lastSoldPriceCompactLabel: string;
    appreciationLabel: string;
    averageDaysListedLabel: string;
  };
  groups: PriceHistoryTimelineMonthGroupView<T>[];
  visibleGroups: PriceHistoryTimelineMonthGroupView<T>[];
  totalRecords: number;
  visibleRecordCount: number;
  hiddenRecordCount: number;
  canExpand: boolean;
  isExpanded: boolean;
  showMore: () => void;
  showLess: () => void;
  toggleExpanded: () => void;
};

type RecordViewBuilderOptions<T extends PriceHistoryListingData> = {
  currentListingId: number | null;
  currency: SupportedCurrency;
  formatDateWithTemplate: (date: Date, template: string) => string;
  locale: string;
  rawByRecord: Map<PriceHistoryRecord, T>;
  t: (key: string, options?: Record<string, unknown>) => string;
};

type GroupBuilderOptions<T extends PriceHistoryListingData> = {
  formatDateWithTemplate: (date: Date, template: string) => string;
  toRecordView: (record: PriceHistoryRecord) => PriceHistoryTimelineRecordView<T>;
  t: (key: string, options?: Record<string, unknown>) => string;
};

type StatsBuilderOptions = {
  currency: SupportedCurrency;
  historicalTimeline: PriceHistoryTimeline;
  locale: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  timeline: PriceHistoryTimeline;
};

// Chooses the number we want to display for a record, preferring sold price over active list price.
function getRecordPrice(record: PriceHistoryListingData): number | null {
  return record.soldPrice ?? record.price ?? null;
}

// Safely converts optional string dates into Date objects for timeline formatting.
function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// Converts one domain record into the formatted strings and media fields the component renders.
function buildRecordView<T extends PriceHistoryListingData>(
  record: PriceHistoryRecord,
  options: RecordViewBuilderOptions<T>
): PriceHistoryTimelineRecordView<T> {
  const {
    currentListingId,
    currency,
    formatDateWithTemplate,
    locale,
    rawByRecord,
    t,
  } = options;
  const raw = rawByRecord.get(record);
  if (!raw) {
    throw new Error('Missing raw listing data for price history record');
  }

  const eventDate = record.eventDate;
  const status = record.status;
  const price = getRecordPrice(raw);
  const priceLabel = formatPrice(Price.from(price), { currency, locale })
    ?? t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NO_DATA_PLACEHOLDER, { defaultValue: '-' });
  const originalPriceLabel = raw.soldPrice && raw.price && raw.soldPrice !== raw.price
    ? formatPrice(Price.from(raw.price), { currency, locale })
    : null;
  const statusLabel = getTimelineStatusLabel({ record: raw, status, t });
  const relativeDateLabel = eventDate ? formatRecency(eventDate, locale) : null;
  const lifecycleDate = toDate(raw.soldAt) ?? toDate(raw.expiredAt);
  const daysOnMarket = record.getDaysListed();
  const daysOnMarketLabel = daysOnMarket === null
    ? '--'
    : getDaysText(daysOnMarket, t as never);
  const lifecycleDateLabel = lifecycleDate
    ? formatDateWithTemplate(lifecycleDate, 'MMM D, YYYY')
    : null;
  const lifecycleLabel = lifecycleDateLabel
    ? (status === StandardStatus.STANDARD_STATUS_CLOSED
      ? t(LOCALE_COMMON_PATHS.PRICE_HISTORY_LIFECYCLE_SOLD_ON, {
        date: lifecycleDateLabel,
        defaultValue: 'Sold on {{date}}',
      })
      : t(LOCALE_COMMON_PATHS.PRICE_HISTORY_LIFECYCLE_CANCELED_ON, {
        date: lifecycleDateLabel,
        defaultValue: 'Canceled on {{date}}',
      }))
    : (raw.isRental
      ? t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NOT_LEASED_YET, { defaultValue: 'Not leased yet' })
      : t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NOT_SOLD_YET, { defaultValue: 'Not sold yet' }));

  return {
    raw,
    id: raw.id,
    addressPath: record.addressPath,
    isCurrentListing: raw.id === currentListingId,
    eventDate,
    absoluteDateLabel: eventDate ? formatDateWithTemplate(eventDate, 'MMM D, YYYY') : '',
    relativeDateLabel,
    status,
    statusLabel,
    price,
    priceLabel,
    originalPriceLabel,
    daysOnMarket,
    daysOnMarketLabel,
    lifecycleDateLabel,
    lifecycleLabel,
    mlsLabel: record.mlsNum ? formatMlsNumber({ mlsNumber: record.mlsNum, providerId: raw.providerId }) : null,
    thumbnailSrc: record.thumbnailUrl || null,
    thumbnailAlt: record.imageDesc ?? '',
  };
}

// Builds month groups from whichever historical record set we want to show.
function buildHistoricalGroups<T extends PriceHistoryListingData>(
  records: PriceHistoryRecord[],
  options: GroupBuilderOptions<T>
): PriceHistoryTimelineMonthGroupView<T>[] {
  const { formatDateWithTemplate, toRecordView, t } = options;
  const recordsTimeline = PriceHistoryTimeline.from(records);

  return recordsTimeline.groupByMonth().map(group => {
    const recordViews = group.records.map(toRecordView);
    const latestRecord = recordViews[0];
    const monthLabel = latestRecord.eventDate
      ? formatDateWithTemplate(latestRecord.eventDate, 'MMMM YYYY')
      : '';
    const groupHeaderLabel = getGroupHeaderLabel({
      statusLabel: latestRecord.statusLabel,
      priceLabel: latestRecord.priceLabel,
      t,
    });

    return {
      monthKey: group.monthKey,
      monthLabel,
      title: [monthLabel, groupHeaderLabel].filter(Boolean).join(', '),
      titleStatus: latestRecord.status,
      titleStatusLabel: latestRecord.statusLabel,
      titlePriceLabel: latestRecord.priceLabel,
      latestRecord,
      records: recordViews,
    };
  });
}

// Builds the today group that shows the current listing data at the top.
function buildTodayGroup<T extends PriceHistoryListingData>(
  record: PriceHistoryRecord,
  toRecordView: (record: PriceHistoryRecord) => PriceHistoryTimelineRecordView<T>,
  t: (key: string, options?: Record<string, unknown>) => string
): PriceHistoryTimelineMonthGroupView<T> {
  const todayRecord = toRecordView(record);
  const todayTitle = t(LOCALE_COMMON_PATHS.PRICE_HISTORY_TIMELINE_GROUP_TODAY_TITLE, {
    status: todayRecord.statusLabel,
    price: todayRecord.priceLabel,
    defaultValue: `Today, ${todayRecord.statusLabel} for ${todayRecord.priceLabel}`,
  });

  return {
    monthKey: 'today',
    monthLabel: t(LOCALE_COMMON_PATHS.PRICE_HISTORY_TIMELINE_TODAY_LABEL, { defaultValue: 'Today' }),
    title: todayTitle,
    titleStatus: todayRecord.status,
    titleStatusLabel: todayRecord.statusLabel,
    titlePriceLabel: todayRecord.priceLabel,
    latestRecord: todayRecord,
    records: [todayRecord],
  };
}

// Formats the three summary stat cards from historical timeline data.
function buildTimelineStats({
  currency,
  historicalTimeline,
  locale,
  t,
  timeline,
}: StatsBuilderOptions) {
  const lastSoldPrice = timeline.getLastSoldPrice();
  const appreciation = historicalTimeline.getAppreciationFromSoldHistory();
  const averageDaysListed = historicalTimeline.getAverageDaysListed();

  return {
    lastSoldPriceLabel: formatPrice(Price.from(lastSoldPrice), { currency, locale }) ?? '--',
    lastSoldPriceCompactLabel: formatPrice(Price.from(lastSoldPrice), { currency, locale, compact: true }) ?? '--',
    appreciationLabel: appreciation.percentage === null
      ? '--'
      : `${appreciation.percentage >= 0 ? '+' : ''}${appreciation.percentage.toFixed(1)}%`,
    averageDaysListedLabel: averageDaysListed === null
      ? '--'
      : getDaysText(Math.round(averageDaysListed), t as never),
  };
}

export function usePriceHistoryTimeline<T extends PriceHistoryListingData>(
  options: UsePriceHistoryTimelineOptions<T>
): UsePriceHistoryTimelineResult<T> {
  const {
    records: inputRecords = [],
    currentListingId,
    visibleCount = DEFAULT_PRICE_HISTORY_VISIBLE_COUNT,
  } = options;
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation(Namespaces.COMMON);
  const { locale, currency, formatDateWithTemplate } = useI18nFormatting();

  // Builds the full UI-ready timeline model from domain records, including stats and grouped rows.
  const timelineData = useMemo(() => {
    const priceHistoryRecords = inputRecords.map(record => PriceHistoryRecord.fromListing(record));
    const rawByRecord = new Map(priceHistoryRecords.map((record, index) => [record, inputRecords[index] as T]));
    const timeline = PriceHistoryTimeline.from(priceHistoryRecords);
    const hasValue = timeline.hasHistory(currentListingId);

    // if there is no history, return an empty timeline
    if (!hasValue) {
      return {
        hasValue: false,
        stats: {
          lastSoldPriceLabel: '--',
          lastSoldPriceCompactLabel: '--',
          appreciationLabel: '--',
          averageDaysListedLabel: '--',
        },
        groups: [],
        visibleGroups: [],
        totalRecords: 0,
      };
    }


    /// Values buildRecordView needs for every timeline row it creates.
    const recordViewBuilderOptions: RecordViewBuilderOptions<T> = {
      currentListingId,
      currency,
      formatDateWithTemplate,
      locale,
      rawByRecord,
      t,
    };
    
    // Values buildHistoricalGroups needs for every month group it creates.
    const groupBuilderOptions: GroupBuilderOptions<T> = {
      formatDateWithTemplate,
      toRecordView: record => buildRecordView(record, recordViewBuilderOptions),
      t,
    };

    const historicalRecordsWithoutCurrent = priceHistoryRecords.filter(record => record.id !== currentListingId);
    const currentListingRecord = priceHistoryRecords.find(record => record.id === currentListingId) ?? null; // this null check is to just make typescript happy
    const todayGroup = buildTodayGroup(currentListingRecord, groupBuilderOptions.toRecordView, t);
    const historicalGroups = buildHistoricalGroups(priceHistoryRecords, groupBuilderOptions);
    const groups = [todayGroup, ...historicalGroups];

    const historicalTimeline = PriceHistoryTimeline.from(historicalRecordsWithoutCurrent);
    // Collapsed history should still allow the current listing to appear again in its natural month group.
    const sortedHistoricalRecords = timeline
      .groupByMonth()
      .flatMap(group => group.records);

    // How many records to show (minus the "today" group itself).
    // Used to limit data before being expanded.
    const visibleHistoricalCount = Math.max(visibleCount - 1, 0);
    const visibleHistoricalRecords = sortedHistoricalRecords.slice(0, visibleHistoricalCount);
    const visibleHistoricalGroups = buildHistoricalGroups(visibleHistoricalRecords, groupBuilderOptions);
    const visibleGroups = [todayGroup, ...visibleHistoricalGroups];

    return {
      hasValue: true,
      stats: buildTimelineStats({ currency, historicalTimeline, locale, t, timeline }),
      groups,
      visibleGroups,
      totalRecords: groups.reduce((total, group) => total + group.records.length, 0),
    };
  }, [inputRecords, currentListingId, currency, locale, formatDateWithTemplate, t, visibleCount]);

  const visibleGroups = isExpanded
    ? timelineData.groups
    : timelineData.visibleGroups;
  const visibleRecordCount = visibleGroups.reduce((total, group) => total + group.records.length, 0);
  const hiddenRecordCount = Math.max(timelineData.totalRecords - visibleRecordCount, 0);
  const canExpand = timelineData.totalRecords > visibleCount;

  return {
    hasValue: timelineData.hasValue,
    stats: timelineData.stats,
    groups: timelineData.groups,
    visibleGroups,
    totalRecords: timelineData.totalRecords,
    visibleRecordCount,
    hiddenRecordCount,
    canExpand,
    isExpanded,
    showMore: () => setIsExpanded(true),
    showLess: () => setIsExpanded(false),
    toggleExpanded: () => setIsExpanded(expanded => !expanded),
  };
}

// Uses the shared standard-status translations to label the timeline chip and group header.
function getTimelineStatusLabel({
  record,
  status,
  t,
}: {
  record: PriceHistoryListingData;
  status: StandardStatus;
  t: (key: string, options?: Record<string, unknown>) => string;
}): string {
  return t(getStatusLabelKey(status, record.isRental ?? false), {
    defaultValue: record.isRental ? 'For Rent' : 'For Sale',
  });
}

// Reuses the standard "status for price" sentence shape for month-group headers.
function getGroupHeaderLabel({
  statusLabel,
  priceLabel,
  t,
}: {
  statusLabel: string;
  priceLabel: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}): string {
  return t(LOCALE_COMMON_PATHS.PRICE_HISTORY_TIMELINE_HEADER_STATUS_FOR, {
    status: statusLabel,
    price: priceLabel,
    defaultValue: `${statusLabel} for ${priceLabel}`,
  });
}
